import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import { ErrorBoundary } from 'react-error-boundary';
import { Switch } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import SwedenMap from './SwedenMap';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Add error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-md text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin  // Register the plugin this way
);

// Add this helper function at the top
const validatePriceData = (data) => {
    if (!data || !data.hourly || !Array.isArray(data.hourly)) {
        return false;
    }
    return true;
};

// Swedish VAT rate for electricity
const VAT_RATE = 0.25; // 25%

// Add this helper function to apply VAT
const applyVat = (price, includeVat) => {
    if (price === null || price === undefined) return null;
    return includeVat ? price * (1 + VAT_RATE) : price;
};

// Add this helper function near the other helper functions at the top
const formatPrice = (price) => {
    return price?.toString().replace('.', ',') ?? '-';
};

// Add these helper functions after validatePriceData
const getCurrentPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const currentHour = new Date().getHours();
    return applyVat(hourlyPrices[currentHour], includeVat);
};

const getLowestPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const withVat = hourlyPrices.map(p => applyVat(p, includeVat));
    const lowest = Math.min(...withVat);
    const hour = withVat.indexOf(lowest);
    return { price: lowest, hour };
};

const getHighestPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const withVat = hourlyPrices.map(p => applyVat(p, includeVat));
    const highest = Math.max(...withVat);
    const hour = withVat.indexOf(highest);
    return { price: highest, hour };
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const pricingStyles = {
    container: {
        background: '#FFFFFF',
        borderRadius: '15px',
        padding: '20px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        color: '#000000',
        fontFamily: 'Arial, sans-serif',
        transition: 'background 0.5s ease',
    },
    header: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#000000',
    },
    gradientBackground: {
        background: 'linear-gradient(135deg, #C8102E 0%, #FECC00 100%)', // Swedish colors
        animation: 'gradient 3s ease infinite',
    },
    '@keyframes gradient': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
    },
    interactiveElement: {
        cursor: 'pointer',
        transition: 'transform 0.2s',
    },
    interactiveElementHover: {
        transform: 'scale(1.05)',
    },
};

const ElectricityPricingContent = ({
    defaultRegion = 'SE3',
    regions = ['SE1', 'SE2', 'SE3', 'SE4'],
    regionColors = {
        'SE1': '#C8102E', // Swedish red
        'SE2': '#C8102E',
        'SE3': '#C8102E',
        'SE4': '#C8102E'
    },
    getRegionColorClass = (region) => {
        const colors = {
            'SE1': 'bg-[#C8102E]',
            'SE2': 'bg-[#C8102E]',
            'SE3': 'bg-[#C8102E]',
            'SE4': 'bg-[#C8102E]'
        };
        return colors[region] || 'bg-[#C8102E]';
    },
    MapComponent = SwedenMap,
    apiEndpoint = '/api/prices',
    countryName = 'Sweden'
}) => {
    const [data, setData] = useState({ today: '', prices: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [predictedPrices, setPredictedPrices] = useState({ tomorrow: {} });
    const [selectedDate, setSelectedDate] = useState('today');
    const [includeVat, setIncludeVat] = useState(false); // Default to excluding VAT

    useEffect(() => {
        const fetchPricesWithRetry = async (retryCount = 0) => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await fetch(apiEndpoint);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                console.log('Fetched Swedish data:', result);
                
                setData(result);
                setPredictedPrices(result.predictedPrices || { tomorrow: {} });
                setLoading(false);
            } catch (err) {
                console.error('Error fetching Swedish prices:', err);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => fetchPricesWithRetry(retryCount + 1), RETRY_DELAY);
                } else {
                    setError(`Failed to load pricing data: ${err.message}`);
                    setLoading(false);
                }
            }
        };

        fetchPricesWithRetry();
    }, [apiEndpoint]);

    if (error) return <div>{error}</div>;

    const generateChartData = (prices) => {
        const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        
        const datasets = Object.entries(prices).flatMap(([region, regionData]) => {
            const hourlyData = regionData?.hourly || [];
            let paddedData = [...hourlyData];
            while (paddedData.length < 24) {
                paddedData.push(null);
            }
            
            const processedData = paddedData.map(price => 
                price !== null ? applyVat(price, includeVat) : null
            );

            return [{
                label: region,
                data: processedData,
                borderColor: regionColors[region] || '#C8102E',
                backgroundColor: (regionColors[region] || '#C8102E') + '20',
                fill: selectedRegion === region,
                tension: 0.1,
                pointRadius: selectedRegion === region ? 4 : 2,
                pointHoverRadius: 6,
                borderWidth: selectedRegion === region ? 3 : 2,
                hidden: selectedRegion !== region,
            }];
        });

        return { labels: hours, datasets };
    };

    const getCurrentDataset = () => {
        switch (selectedDate) {
            case 'tomorrow':
                return predictedPrices.tomorrow;
            default:
                return data.prices;
        }
    };

    const getFormattedDate = () => {
        const date = new Date();
        switch (selectedDate) {
            case 'tomorrow':
                date.setDate(date.getDate() + 1);
                break;
            default:
                break;
        }
        const formatted = date.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        return formatted;
    };

const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 100,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            annotation: {
                annotations: {
                    currentTime: {
                        xScaleID: 'x',
                        display: selectedDate === 'today',
                        type: 'line',
                        xMin: new Date().getHours(),
                        xMax: new Date().getHours(),
                        borderColor: '#FF0000',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            display: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: '#FFFFFF',
                            content: (ctx) => {
                                const currentPrice = getCurrentPrice(data.prices[selectedRegion]?.hourly, includeVat);
                                return [`Nu`, `${formatPrice((currentPrice)?.toFixed(2))} öre/kWh`];
                            },
                            position: 'start',
                            padding: 6,
                            font: {
                                size: 12
                            },
                            xAdjust: 0,
                            yAdjust: 0,
                            overflow: 'fit',
                            enabled: (ctx) => {
                                return !!(ctx && ctx.chart && ctx.chart.chartArea && 
                                        typeof ctx.chart.chartArea.left === 'number' && 
                                        typeof ctx.chart.chartArea.right === 'number');
                            },
                        },
                        beforeDraw: (ctx) => {
                            if (!ctx || !ctx.chart || !ctx.chart.chartArea || 
                                typeof ctx.chart.chartArea.left === 'undefined') {
                                return false;
                            }
                            return true;
                        },
                        afterDraw: (ctx) => {
                            return !!ctx?.chart?.chartArea;
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#FFFFFF',
                bodyColor: '#FFFFFF',
                padding: 12,
                titleFont: {
                    size: 14,
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                callbacks: {
                    label: function(tooltipItem) {
                        const timeRange = `${tooltipItem.label}–${(parseInt(tooltipItem.label) + 1) % 24}:00`;
                        const currentHour = new Date().getHours();
                        const isCurrentHour = parseInt(tooltipItem.label) === currentHour;
                        const statusText = isCurrentHour ? ' (nu)' : '';
                        return `${timeRange}: ${formatPrice(tooltipItem.formattedValue)} öre/kWh${statusText}`;
                    },
                    title: function(tooltipItems) {
                        if (tooltipItems.length > 0) {
                            const hour = parseInt(tooltipItems[0].label);
                            return `Kl. ${formatHour(hour)}`;
                        }
                        return '';
                    }
                }
            },
            legend: {
                display: false,
            }
        },
        scales: {
            y: {
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    drawTicks: false
                },
                ticks: {
                    color: '#000000',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return formatPrice(value.toFixed(0)) + ' öre';
                    }
                },
                beginAtZero: false,
                suggestedMin: function(context) {
                    const data = context.chart.data;
                    const allValues = data.datasets.flatMap(dataset => 
                        dataset.data.filter(val => val !== null && !isNaN(val))
                    );
                    if (allValues.length === 0) return 0;
                    const min = Math.min(...allValues);
                    return Math.max(0, min - (min * 0.1));
                },
                suggestedMax: function(context) {
                    const data = context.chart.data;
                    const allValues = data.datasets.flatMap(dataset => 
                        dataset.data.filter(val => val !== null && !isNaN(val))
                    );
                    if (allValues.length === 0) return 100;
                    const max = Math.max(...allValues);
                    return max + (max * 0.1);
                }
            },
            x: {
                border: {
                    display: false
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: '#000000',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    maxRotation: 0,
                    callback: function(value, index) {
                        if (index % 2 === 0) {
                            return this.getLabelForValue(value);
                        }
                        return '';
                    }
                }
            }
        }
    };

    const MemoizedChart = React.memo(({ data, options }) => (
        <Line data={data} options={options} redraw={false} />
    ));

    const formattedStr = data.today ? `${data.today.slice(0, 4)}-${data.today.slice(4, 6)}-${data.today.slice(6, 8)}` : '';
    const formattedDate = formattedStr ? (() => {
        const date = new Date(formattedStr);
        return date.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    })() : 'Invalid date';

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
    };

    // Add region dropdown change handler
    const handleRegionChange = (e) => {
        setSelectedRegion(e.target.value);
    };

    // Improve the getChartHeight function for better mobile experience
    const getChartHeight = () => {
        // Much taller on mobile devices, especially Chrome
        if (window.innerWidth < 480) {
            return "h-[400px]"; // Significantly taller on small mobile
        } else if (window.innerWidth < 768) {
            return "h-[450px]"; // Taller on larger mobile
        }
        return "h-80 md:h-[calc(100%-80px)]"; // Original height on desktop
    };

    // Add a function to determine if we're on mobile Chrome
    const isMobileChrome = () => {
        return /Chrome/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent);
    };

    // Add error boundary for chart rendering
    const renderChart = () => {
        try {
            const chartData = generateChartData(
                selectedRegion === 'ALL' 
                    ? getCurrentDataset() 
                    : { [selectedRegion]: getCurrentDataset()[selectedRegion] }
            );

            if (!chartData || !chartData.datasets || !chartData.labels) {
                console.error('Invalid chart data:', chartData);
                return <div>Error: Invalid data format</div>;
            }

            // Enhanced mobile Chrome options
            if (isMobileChrome()) {
                const mobileOptions = {
                    ...chartOptions,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            top: 10,
                            right: 10,
                            bottom: 10,
                            left: 10
                        }
                    },
                    plugins: {
                        ...chartOptions.plugins,
                        annotation: {
                            annotations: {} // Empty annotations on mobile Chrome
                        }
                    },
                    scales: {
                        ...chartOptions.scales,
                        y: {
                            ...chartOptions.scales.y,
                            // Force a more expanded range for mobile
                            ticks: {
                                ...chartOptions.scales.y.ticks,
                                count: 5, // Limit number of ticks for clarity
                            },
                            // More aggressive adjustment for mobile
                            suggestedMin: function(context) {
                                if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 0;
                                
                                const allValues = context.chart.data.datasets
                                    .flatMap(dataset => dataset.data)
                                    .filter(val => val !== null && val !== undefined)
                                    .map(val => parseFloat(val));
                                
                                if (allValues.length === 0) return 0;
                                const min = Math.min(...allValues);
                                return Math.max(0, min * 0.7); // More space below on mobile
                            },
                            suggestedMax: function(context) {
                                if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 100;
                                
                                const allValues = context.chart.data.datasets
                                    .flatMap(dataset => dataset.data)
                                    .filter(val => val !== null && val !== undefined)
                                    .map(val => parseFloat(val));
                                
                                if (allValues.length === 0) return 100;
                                const max = Math.max(...allValues);
                                return max * 1.3; // More space above on mobile
                            }
                        }
                    }
                };

                return (
                    <div className="relative w-full h-full flex items-center justify-center">
                        <MemoizedChart 
                            data={chartData} 
                            options={mobileOptions}
                            className="w-full h-full" 
                        />
                    </div>
                );
            }

            return (
                <div className="w-full h-full">
                    <MemoizedChart 
                        data={chartData} 
                        options={chartOptions}
                        className="w-full h-full"
                    />
                </div>
            );
        } catch (error) {
            console.error('Chart rendering error:', error);
            return <div>Error rendering chart</div>;
        }
    };

    return (
        <div className="min-h-screen bg-[#f0f0f0]">
            <div className="container mx-auto px-4 py-8 max-w-6xl">
                {/* Mobile region selector */}
                <div className="md:hidden mb-3">
                    <label htmlFor="region-select" className="block text-md font-medium text-[#C8102E] mb-2">
                        Välj prisområde
                    </label>
                    <div className="relative">
                        <select
                            id="region-select"
                            value={selectedRegion}
                            onChange={handleRegionChange}
                            className="block w-full appearance-none bg-white border border-gray-200 text-[#C8102E] py-3 px-4 pr-8 rounded-xl leading-tight focus:outline-none focus:ring-2 focus:ring-[#C8102E]/30"
                        >
                            {Object.keys(data.prices || {}).map((area) => (
                                <option key={area} value={area}>
                                    {area} - {formatPrice((applyVat(parseFloat(data.prices[area]?.average || 0), includeVat)).toFixed(1))} öre/kWh
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-[#C8102E]">
                            <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                        </div>
                    </div>
                </div>
                
                {/* Desktop region buttons */}
                <div className="hidden md:grid grid-cols-4 gap-2 mb-6">
                    {!loading && Object.entries(data.prices).map(([area, priceData]) => (
                        <button
                            key={area}
                            onClick={() => handleRegionSelect(area)}
                            className={`
                                p-2 rounded-xl transition-all duration-300
                                ${selectedRegion === area 
                                    ? 'bg-[#C8102E] text-white shadow-lg font-bold scale-102' 
                                    : 'text-[#000] hover:bg-[#C8102E] bg-white font-bold hover:text-white'}
                            `}
                        >
                            <h3 className="font-medium mb-1">
                                {area} - {{
                                    'SE1': 'Luleå / Norra Sverige',
                                    'SE2': 'Sundsvall / Norra Mellansverige',
                                    'SE3': 'Stockholm / Södra Mellansverige',
                                    'SE4': 'Malmö / Södra Sverige'
                                }[area] || area}
                            </h3>
                            <p className="text-lg font-light">
                                {formatPrice((applyVat(parseFloat(priceData?.average || 0), includeVat)).toFixed(1))}
                                <span className="text-md ml-1 opacity-60">öre/kWh</span>
                            </p>
                        </button>
                    ))}
                </div>

                {/* Main content grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Chart section */}
                    <div className="lg:col-span-8">
                        {/* Chart container */}
                        <div className="bg-white rounded-xl p-4 md:p-6 h-full flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6">
                                <h2 className="text-xl md:text-2xl font-light text-black mb-4 md:mb-0">
                                    {getFormattedDate()}
                                </h2>
                                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                                    {/* VAT toggle */}
                                    <div className="flex items-center gap-3">
                                        <span className="text-md font-medium text-black">
                                            {includeVat ? 'Inkl. moms (25%)' : 'Exkl. moms'}
                                        </span>
                                        <Switch
                                            checked={includeVat}
                                            onChange={setIncludeVat}
                                            className={`${
                                                includeVat ? 'bg-[#C8102E]' : 'bg-[#eff2f3]'
                                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#C8102E]/20`}
                                        >
                                            <span
                                                className={`${
                                                    includeVat ? 'translate-x-6' : 'translate-x-1'
                                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                            />
                                        </Switch>
                                    </div>
                                </div>
                            </div>
                            <div className={`${getChartHeight()} flex-grow`}>
                                {loading ? (
                                    <div className="animate-pulse h-full bg-[#eff2f3] rounded-lg" />
                                ) : (
                                    renderChart()
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Map section */}
                    <div className="hidden lg:block lg:col-span-4">
                        <div className="bg-white rounded-xl p-4 md:p-6 h-full">
                            <h3 className="text-xl font-medium text-black mb-6">Prisområden</h3>
                            <div className="h-[calc(100%-50px)]">
                                <MapComponent 
                                    onRegionSelect={handleRegionSelect} 
                                    selectedRegion={selectedRegion}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Price cards */}
                    <div className="lg:col-span-12">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {loading ? (
                                Array(3).fill(0).map((_, index) => (
                                    <div key={index} className="animate-pulse bg-white/80 backdrop-blur rounded-xl h-[100px]" />
                                ))
                            ) : (
                                <>
                                    <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                        <h3 className="text-black font-medium mb-1">Nuvarande pris</h3>
                                        <p className="text-2xl font-light text-black">
                                            {formatPrice((getCurrentPrice(data.prices[selectedRegion]?.hourly, includeVat))?.toFixed(2))}
                                            <span className="text-xs ml-1">öre/kWh</span>
                                            <span className="text-xs ml-2">
                                                {includeVat ? '(inkl. moms)' : '(exkl. moms)'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                        <h3 className="text-black font-medium mb-1">Lägsta pris</h3>
                                        <p className="text-2xl font-light text-black">
                                            {formatPrice((getLowestPrice(data.prices[selectedRegion]?.hourly, includeVat)?.price)?.toFixed(2))}
                                            <span className="text-xs ml-1">öre/kWh</span>
                                            <span className="text-xs ml-2">
                                                {includeVat ? '(inkl. moms)' : '(exkl. moms)'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                        <h3 className="text-black font-medium mb-1">Högsta pris</h3>
                                        <p className="text-2xl font-light text-black">
                                            {formatPrice((getHighestPrice(data.prices[selectedRegion]?.hourly, includeVat)?.price)?.toFixed(2))}
                                            <span className="text-xs ml-1">öre/kWh</span>
                                            <span className="text-xs ml-2">
                                                {includeVat ? '(inkl. moms)' : '(exkl. moms)'}
                                            </span>
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrap main component with ErrorBoundary
const CheapSwedenPricing = (props) => {
    return (
        <ErrorBoundary 
            FallbackComponent={ErrorFallback}
            onReset={() => window.location.reload()}
        >
            <Helmet>
                <title>Cheapenergy Sverige</title>
                <link rel="canonical" href="https://cheapenergy.se/elpriser/" />
            </Helmet>
            <ElectricityPricingContent {...props} />
        </ErrorBoundary>
    );
};

// Export the component wrapped in React.memo
export default React.memo(CheapSwedenPricing);

// Add this helper function for region colors
const getRegionColorClass = (region) => {
    const colors = {
        'SE1': 'bg-[#C8102E]',
        'SE2': 'bg-[#C8102E]',
        'SE3': 'bg-[#C8102E]',
        'SE4': 'bg-[#C8102E]'
    };
    return colors[region] || 'bg-[#C8102E]';
};

export const PriceCards = ({ data, loading, selectedRegion, getRegionColorClass, getCurrentPrice, getLowestPrice, getHighestPrice, formatHour, includeVat }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {loading ? (
                Array(3).fill(0).map((_, index) => (
                    <div key={index} className="animate-pulse bg-white/5 rounded-lg h-[80px] md:h-[100px]" />
                ))
            ) : (
                <>
                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-md md:text-base font-bold mb-1">Nuvarande pris</h3>
                                <p className="text-md md:text-md text-black">
                                    Nu {includeVat ? '(inkl. moms)' : '(exkl. moms)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice(getCurrentPrice(data.prices[selectedRegion]?.hourly, includeVat)?.toFixed(1) ?? '-')}
                                <span className="text-md md:text-md opacity-70 ml-1">öre/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-md md:text-base font-bold mb-1">Lägsta pris</h3>
                                <p className="text-md md:text-md text-black">
                                    Kl. {formatHour(getLowestPrice(data.prices[selectedRegion]?.hour, includeVat)?.hour)}
                                    {includeVat ? ' (inkl. moms)' : ' (exkl. moms)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice((getLowestPrice(data.prices[selectedRegion]?.hourly, includeVat)?.price).toFixed(1) ?? '-')}
                                <span className="text-md md:text-md opacity-70 ml-1">öre/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-md md:text-base font-bold mb-1">Högsta pris</h3>
                                <p className="text-md md:text-md text-black">
                                    Kl. {formatHour(getHighestPrice(data.prices[selectedRegion]?.hour, includeVat)?.hour)}
                                    {includeVat ? ' (inkl. moms)' : ' (exkl. moms)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice((getHighestPrice(data.prices[selectedRegion]?.hourly, includeVat)?.price).toFixed(1) ?? '-')}
                                <span className="text-md md:text-md text-black ml-1">öre/kWh</span>
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
